import * as THREE from 'three';

export class Hologram {
    public mesh: THREE.Group;
    private globe!: THREE.LineSegments;
    private core!: THREE.Mesh;
    private rings: THREE.Mesh[] = [];
    private locations!: THREE.Group;

    constructor() {
        this.mesh = new THREE.Group();
        this.mesh.name = 'hologram_globe';

        this.createGlobe();
        this.createRings();
        this.createCore();
        this.addLocations();

        // Tilt the entire assembly slightly for a better angle
        this.mesh.rotation.z = Math.PI / 6;
        this.mesh.rotation.x = Math.PI / 8;
    }

    private createGlobe(): void {
        const geometry = new THREE.IcosahedronGeometry(0.8, 2);
        const wireframe = new THREE.WireframeGeometry(geometry);

        const material = new THREE.LineBasicMaterial({
            color: 0x00d4ff,
            transparent: true,
            opacity: 0.3,
            side: THREE.BackSide
        });

        this.globe = new THREE.LineSegments(wireframe, material);
        this.globe.layers.enableAll();
        this.mesh.add(this.globe);
    }

    private createCore(): void {
        // glowing core
        const geometry = new THREE.IcosahedronGeometry(0.5, 1);
        const material = new THREE.MeshBasicMaterial({
            color: 0x00d4ff,
            transparent: true,
            opacity: 0.1,
            wireframe: false
        });
        this.core = new THREE.Mesh(geometry, material);
        this.mesh.add(this.core);
    }

    private createRings(): void {
        const ringConfigs = [
            { radius: 1.2, speed: 0.2, color: 0x7c3aed }, // Purple ring
            { radius: 1.4, speed: -0.15, color: 0x00ff88 }, // Green ring
            { radius: 1.6, speed: 0.1, color: 0x00d4ff }  // Cyan ring
        ];

        ringConfigs.forEach(config => {
            const geometry = new THREE.RingGeometry(config.radius, config.radius + 0.02, 64);
            const material = new THREE.MeshBasicMaterial({
                color: config.color,
                transparent: true,
                opacity: 0.4,
                side: THREE.DoubleSide
            });

            const ring = new THREE.Mesh(geometry, material);
            ring.userData = { speed: config.speed };
            ring.rotation.x = Math.PI / 2; // Flat initially

            // Add some random initial tilt
            ring.rotation.x += (Math.random() - 0.5) * 0.5;
            ring.rotation.y += (Math.random() - 0.5) * 0.5;

            this.rings.push(ring);
            this.mesh.add(ring);
        });
    }

    private addLocations(): void {
        this.locations = new THREE.Group();

        // Approximate 3D coordinates on a sphere of radius 0.8
        // Latitude/Longitude to Vector3 conversion would be ideal, but for now fixed points work for the "sci-fi" look
        const points = [
            { lat: 22.3, lng: 114.1, color: 0xff00ff, label: "HK" }, // Hong Kong
            { lat: 1.3, lng: 103.8, color: 0x00ff88, label: "SG" },  // Singapore
            { lat: 35.6, lng: 139.6, color: 0x00d4ff, label: "JP" }  // Tokyo
        ];

        const radius = 0.8;

        points.forEach(p => {
            // Convert lat/lng to vector3
            const phi = (90 - p.lat) * (Math.PI / 180);
            const theta = (p.lng + 180) * (Math.PI / 180);

            const x = radius * Math.sin(phi) * Math.cos(theta); // Invert z/x for three.js coord system usually
            const z = radius * Math.sin(phi) * Math.sin(theta);
            const y = radius * Math.cos(phi);

            // Dot
            const dotGeo = new THREE.SphereGeometry(0.03, 8, 8);
            const dotMat = new THREE.MeshBasicMaterial({ color: p.color });
            const dot = new THREE.Mesh(dotGeo, dotMat);
            dot.position.set(-x, y, z); // Adjust coordinate mapping for visual correctness

            this.locations.add(dot);

            // Pulse Ring for location
            const pulseGeo = new THREE.RingGeometry(0.04, 0.05, 32);
            const pulseMat = new THREE.MeshBasicMaterial({ color: p.color, transparent: true, opacity: 0.8, side: THREE.DoubleSide });
            const pulse = new THREE.Mesh(pulseGeo, pulseMat);
            pulse.position.copy(dot.position);
            pulse.lookAt(0, 0, 0); // Face center
            this.locations.add(pulse);
        });

        this.mesh.add(this.locations);
    }

    public update(deltaTime: number): void {
        const time = Date.now() * 0.001;

        // Rotate globe
        this.globe.rotation.y += 0.1 * deltaTime;
        this.locations.rotation.y += 0.1 * deltaTime; // Locations rotate with globe
        this.core.rotation.y -= 0.05 * deltaTime;

        // Rotate rings
        this.rings.forEach((ring, i) => {
            const speed = ring.userData.speed;
            ring.rotation.z += speed * deltaTime;

            // Gentle wobble
            ring.rotation.x += Math.sin(time + i) * 0.002;
        });

        // Pulse effect
        const pulseScale = 1 + Math.sin(time * 3) * 0.05;
        this.core.scale.setScalar(pulseScale);
    }
}
